import { useState } from 'react';
import { FolderKanban, AlertTriangle, Check, Plus, ChevronDown, ChevronRight, Pause, Archive } from 'lucide-react';

function ProjectCard({
  project,
  needsNextAction,
  isReviewed,
  onMarkReviewed,
  onAddNextAction,
  onUpdateProject
}) {
  const [isExpanded, setIsExpanded] = useState(needsNextAction);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [isAddingAction, setIsAddingAction] = useState(false);

  const handleAddAction = async () => {
    if (!newActionTitle.trim()) return;
    await onAddNextAction(project.id, newActionTitle.trim());
    setNewActionTitle('');
    setIsAddingAction(false);
    onMarkReviewed(project.id);
  };

  const handleStatusChange = async (newStatus) => {
    await onUpdateProject(project.id, { status: newStatus });
    onMarkReviewed(project.id);
  };

  return (
    <div className={`
      bg-surface-card rounded-xl border transition-all
      ${needsNextAction && !isReviewed
        ? 'border-warning/50 bg-warning/5'
        : isReviewed
          ? 'border-success/50'
          : 'border-border'
      }
    `}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-4 text-left bg-transparent border-none cursor-pointer"
      >
        <div className={`
          w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
          ${needsNextAction && !isReviewed
            ? 'bg-warning/20'
            : isReviewed
              ? 'bg-success/20'
              : 'bg-surface-hover'
          }
        `}>
          {isReviewed ? (
            <Check size={16} className="text-success" />
          ) : needsNextAction ? (
            <AlertTriangle size={16} className="text-warning" />
          ) : (
            <FolderKanban size={16} className="text-text-secondary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-primary truncate">{project.name}</p>
          {project.description && (
            <p className="text-xs text-text-tertiary truncate">{project.description}</p>
          )}
        </div>
        {needsNextAction && !isReviewed && (
          <span className="text-xs text-warning bg-warning/10 px-2 py-0.5 rounded-full">
            Needs action
          </span>
        )}
        {isExpanded ? <ChevronDown size={16} className="text-text-tertiary" /> : <ChevronRight size={16} className="text-text-tertiary" />}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border/50">
          {/* Add next action */}
          {needsNextAction && !isReviewed && (
            <div className="mt-3">
              {isAddingAction ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newActionTitle}
                    onChange={(e) => setNewActionTitle(e.target.value)}
                    placeholder="What's the next action?"
                    className="flex-1 px-3 py-2 text-sm bg-surface-base border border-border rounded-lg
                             focus:outline-none focus:border-accent text-text-primary"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleAddAction()}
                  />
                  <button
                    onClick={handleAddAction}
                    disabled={!newActionTitle.trim()}
                    className="px-3 py-2 text-sm bg-accent text-white rounded-lg hover:bg-orange-hover
                             disabled:opacity-50 disabled:cursor-not-allowed border-none cursor-pointer"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setIsAddingAction(false);
                      setNewActionTitle('');
                    }}
                    className="px-3 py-2 text-sm text-text-secondary hover:text-text-primary
                             bg-transparent border-none cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingAction(true)}
                  className="flex items-center gap-2 text-sm text-accent hover:text-orange-hover
                           bg-transparent border-none cursor-pointer"
                >
                  <Plus size={16} />
                  Add Next Action
                </button>
              )}
            </div>
          )}

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
            <span className="text-xs text-text-tertiary">Change status:</span>
            <button
              onClick={() => handleStatusChange('on-hold')}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-warning
                       bg-surface-hover px-2 py-1 rounded-md border-none cursor-pointer"
            >
              <Pause size={12} />
              On Hold
            </button>
            <button
              onClick={() => handleStatusChange('completed')}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-success
                       bg-surface-hover px-2 py-1 rounded-md border-none cursor-pointer"
            >
              <Check size={12} />
              Complete
            </button>
            {!isReviewed && !needsNextAction && (
              <button
                onClick={() => onMarkReviewed(project.id)}
                className="ml-auto text-xs text-accent hover:text-orange-hover
                         bg-transparent border-none cursor-pointer"
              >
                Mark Reviewed
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectReview({
  projects,
  projectsWithoutNextActions,
  reviewedProjects,
  onMarkReviewed,
  onAddNextAction,
  onUpdateProject
}) {
  const needsAttentionIds = new Set(projectsWithoutNextActions.map(p => p.id));
  const reviewedCount = reviewedProjects.size;
  const totalNeedingAttention = projectsWithoutNextActions.length;

  // Sort: projects needing attention first, then unreviewd, then reviewed
  const sortedProjects = [...projects].sort((a, b) => {
    const aNeeds = needsAttentionIds.has(a.id) && !reviewedProjects.has(a.id);
    const bNeeds = needsAttentionIds.has(b.id) && !reviewedProjects.has(b.id);
    const aReviewed = reviewedProjects.has(a.id);
    const bReviewed = reviewedProjects.has(b.id);

    if (aNeeds && !bNeeds) return -1;
    if (!aNeeds && bNeeds) return 1;
    if (aReviewed && !bReviewed) return 1;
    if (!aReviewed && bReviewed) return -1;
    return 0;
  });

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-weekly/10 flex items-center justify-center mx-auto mb-4">
            <FolderKanban size={32} className="text-weekly" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">Review Projects</h2>
          <p className="text-text-secondary">
            Ensure every active project has at least one clear next action.
          </p>
        </div>

        {/* Progress */}
        {totalNeedingAttention > 0 && (
          <div className="mb-6 p-4 bg-warning/10 rounded-xl border border-warning/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-warning">Projects Needing Attention</span>
              <span className="text-sm text-warning">
                {Math.max(0, totalNeedingAttention - [...reviewedProjects].filter(id => needsAttentionIds.has(id)).length)} remaining
              </span>
            </div>
            <div className="h-2 bg-warning/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-warning transition-all duration-300"
                style={{
                  width: `${totalNeedingAttention > 0
                    ? ([...reviewedProjects].filter(id => needsAttentionIds.has(id)).length / totalNeedingAttention) * 100
                    : 0}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Project list */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-tertiary">No active projects to review</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedProjects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                needsNextAction={needsAttentionIds.has(project.id)}
                isReviewed={reviewedProjects.has(project.id)}
                onMarkReviewed={onMarkReviewed}
                onAddNextAction={onAddNextAction}
                onUpdateProject={onUpdateProject}
              />
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 text-center text-sm text-text-tertiary">
          {reviewedCount} of {projects.length} projects reviewed
        </div>
      </div>
    </div>
  );
}
